import type { ElectricityConsumptionNarrativeInput } from './electricityConsumptionNarrative';
import { buildElectricityNarrativeFacts } from './electricityConsumptionNarrative';
import {
  compareToReference,
  formatComparatorPhrase,
  isFiniteNumber,
} from './narrativeFormatting';
import { estimatedYearsKeyInsightsSentence } from './energyEstimatedYearsCopy';
import { buildTrendSentence } from './optionalClauseTokens';
import type { NarrativeTokenMap } from './narrativeTemplateEngine';

/** Template already appends " GWh"; strip unit from headline formatter if present. */
function gwhTokenValue(kwh: number, formatGwh: (kwh: number) => string): string {
  return formatGwh(kwh).replace(/\s*GWh\s*$/i, '').trim();
}

export function buildElectricityNarrativeTokens(
  input: ElectricityConsumptionNarrativeInput
): NarrativeTokenMap {
  const facts = buildElectricityNarrativeFacts(input);
  const loc = input.locationName;
  const baselineYear = facts.baselineYear ?? 2005;
  const estimatedYearsSentence = estimatedYearsKeyInsightsSentence(Boolean(input.hasEstimatedYears));

  const base: NarrativeTokenMap = {
    locationName: loc,
    latestYear: facts.currentYear,
    baselineYear,
    estimatedYearsSentence,
    hasEstimatedYears: input.hasEstimatedYears ? 'yes' : '',
  };

  if (input.viewId === 'perCapita') {
    if (!input.perCapita) {
      return { ...base, localKwhPerPerson: null };
    }
    const { year, localKwhPerPerson, californiaKwhPerPerson } = input.perCapita;
    const cmp = compareToReference(localKwhPerPerson, californiaKwhPerPerson);
    const californiaComparisonSentence =
      cmp.relation !== 'unavailable' && californiaKwhPerPerson != null
        ? ` Compared with ${Math.round(californiaKwhPerPerson)} kWh per resident statewide (${formatComparatorPhrase(cmp.relation, 'California')}).`
        : '';
    return {
      ...base,
      latestYear: year,
      localKwhPerPerson: Math.round(localKwhPerPerson),
      percentChange: facts.percentChange,
      trendSentence: buildTrendSentence(facts.percentChange, baselineYear),
      trendPhrase: '',
      californiaComparisonSentence,
      californiaKwhPerPerson:
        californiaKwhPerPerson != null ? Math.round(californiaKwhPerPerson) : null,
    };
  }

  const sector =
    input.viewId === 'res' ? input.residential : input.viewId === 'com' ? input.commercial : input.total;
  if (!sector || sector.latestValueKwh <= 0) {
    return base;
  }

  const gwh = gwhTokenValue(sector.latestValueKwh, input.formatGwh);
  const pct = sector.changePercentSinceBaseline;
  const resShare = facts.componentShares?.residential;
  const comShare = facts.componentShares?.commercial;

  if (input.viewId === 'total') {
    return {
      ...base,
      latestYear: sector.latestYear,
      totalGWh: gwh,
      totalChangePercent: pct,
      trendSentence: buildTrendSentence(pct, baselineYear),
      trendPhrase: '',
      residentialShare: resShare != null ? resShare.toFixed(1) : null,
      commercialShare: comShare != null ? comShare.toFixed(1) : null,
    };
  }

  if (input.viewId === 'res') {
    const share = facts.componentShares?.residentialShareOfPair;
    return {
      ...base,
      latestYear: sector.latestYear,
      residentialGWh: gwh,
      residentialChangePercent: pct,
      trendSentence: buildTrendSentence(pct, baselineYear),
      trendPhrase: '',
      residentialShare: share != null ? share.toFixed(1) : null,
    };
  }

  const share = facts.componentShares?.commercialShareOfPair;
  return {
    ...base,
    latestYear: sector.latestYear,
    commercialGWh: gwh,
    commercialChangePercent: pct,
    trendSentence: buildTrendSentence(pct, baselineYear),
    trendPhrase: '',
    commercialShare: share != null ? share.toFixed(1) : null,
    commercialLabel: input.commercialLabel ?? 'Reported commercial',
  };
}
